import { useCallback, useEffect, useRef } from "react";

/**
 * BeforeAfterSection — static before/after slider, full container width.
 *
 * Layers (DOM order = z-stack, bottom → top):
 *   1. <picture><img class="cdw-ba-img-before"> — static landscape image (BEFORE,
 *      the "generic" / pre-workshop screenshot). No motion.
 *   2. <picture><img class="cdw-ba-img-after"> — static landscape image (AFTER),
 *      clipped via `clip-path: polygon(--pos 0, 100% 0, 100% 100%, --pos 100%)`
 *      so dragging --pos right reveals more of the BEFORE underneath.
 *   3. Handle (vertical hairline + circular knob) — pointer-events: none.
 *
 * Interactions:
 *   - Drag (mouse + touch via Pointer Events) on the slider container updates
 *     --pos directly on the slider DOM element (no React re-render).
 *   - Scroll-triggered intro: when slider first enters viewport, --pos animates
 *     from 0% → 50% over 1400ms easeOutCubic (one-shot).
 *   - prefers-reduced-motion: reduce → intro animation skipped, --pos jumps to 50%.
 */

const BA_BEFORE_AVIF = "/images/design-workshop/ba-redesign-before.avif";
const BA_BEFORE_WEBP = "/images/design-workshop/ba-redesign-before.webp";
const BA_BEFORE_WEBP_800 = "/images/design-workshop/ba-redesign-before-800.webp";
const BA_AFTER_AVIF = "/images/design-workshop/ba-redesign-after.avif";
const BA_AFTER_WEBP = "/images/design-workshop/ba-redesign-after.webp";
const BA_AFTER_WEBP_800 = "/images/design-workshop/ba-redesign-after-800.webp";

/* A DS dark room, with fallbacks so the pre-reskin page still renders.
   This section was a warm near-black ground (hsl(60,3%,8%)) with `var(--terra)`
   for every accent, and on the reskinned page that combination is unreadable:
   --terra is the lead family's 400 step, which the system sizes for PAPER —
   7.3:1 on cream, but 2.3:1 against this ground. The eyebrow, the rule, the
   "This workshop." emphasis and the slider handle all disappeared into it.

   The system's answer is the 800 step for anything on a dark ground and the
   900 for display, so that is what these now resolve to: accent 6.1:1 and
   heading 8.9:1 against the room's own ground. */
const GROUND = "var(--indigo, hsl(60, 3%, 8%))";
const DISPLAY = "var(--ds-lead-900, hsl(48, 20%, 92%))";
const ACCENT = "var(--ds-lead-800, var(--terra))";
/* Sits ON the accent (the knob), so it takes the ground step, not white —
   white on the 800 is 1.9:1. */
const ON_ACCENT = "var(--ds-lead-300, #fff)";

// Inline `--*` CSS custom properties aren't in React.CSSProperties by default.
type CSSVars = React.CSSProperties & {
  ["--pos"]?: string;
};

export function BeforeAfterSection() {
  const sliderRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef(false);
  const reducedRef = useRef(false);

  /** Write --pos directly on the slider element (avoids React state churn during drag). */
  const setPos = useCallback((pct: number) => {
    const el = sliderRef.current;
    if (!el) return;
    el.style.setProperty("--pos", `${pct.toFixed(2)}%`);
  }, []);

  const setPosFromClientX = useCallback((clientX: number) => {
    const el = sliderRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setPos(pct);
  }, [setPos]);

  // Cache prefers-reduced-motion in a ref so the IO effect can read synchronously.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedRef.current = mq.matches;
    const handler = (e: MediaQueryListEvent) => {
      reducedRef.current = e.matches;
    };
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  // Pointer drag — mouse + touch via Pointer Events.
  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.currentTarget.classList.add("is-dragging", "has-interacted");
    setPosFromClientX(e.clientX);
  }, [setPosFromClientX]);
  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    setPosFromClientX(e.clientX);
  }, [setPosFromClientX]);
  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    e.currentTarget.classList.remove("is-dragging");
  }, []);

  // Scroll-triggered intro: --pos animates 0% → 50% over 1400ms easeOutCubic
  // when the slider first enters the viewport.
  useEffect(() => {
    const el = sliderRef.current;
    if (!el) return;

    setPos(0); // ensure initial CSS var is set on mount

    let animated = false;
    const animateIntro = () => {
      if (animated) return;
      animated = true;
      if (reducedRef.current) {
        setPos(50);
        return;
      }
      const duration = 1400;
      const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
      let start: number | null = null;
      const tick = (ts: number) => {
        if (start === null) start = ts;
        const t = Math.min((ts - start) / duration, 1);
        setPos(50 * easeOutCubic(t));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            animateIntro();
          }
        }
      },
      // Trigger the slider intro 300 px before the section enters the
      // viewport so the animation completes before the user can see it —
      // catching it mid-flight on a fast scroll feels like a speed bump.
      { threshold: 0, rootMargin: "0px 0px 300px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [setPos]);

  const sliderInline: CSSVars = {
    position: "relative",
    width: "100%",
    aspectRatio: "16 / 9",
    background: "var(--indigo-deep, #1a1814)",
    cursor: "grab",
    userSelect: "none",
    touchAction: "pan-y",
    overflow: "hidden",
    borderRadius: 10,
    "--pos": "0%",
  };

  return (
    <section
      style={{
        background: GROUND,
        color: DISPLAY,
        padding: "120px 0",
        position: "relative",
        overflow: "hidden",
      }}
      data-section="before-after"
    >
      <div style={{ maxWidth: 1340, margin: "0 auto", padding: "0 56px" }}>
        {/* Eyebrow */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.32em",
              textTransform: "uppercase",
              color: ACCENT,
              fontWeight: 600,
            }}
          >
            Before vs. After
          </span>
          <span style={{ height: 1, width: 120, background: ACCENT, opacity: 0.5 }} />
        </div>

        {/* Header */}
        <div style={{ marginBottom: 60 }} className="cdw-ba-header-grid">
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 500,
              fontSize: "clamp(36px, 5vw, 54px)",
              lineHeight: 1.3,
              letterSpacing: "-0.06em",
              color: DISPLAY,
              maxWidth: "19ch",
              margin: 0,
              textWrap: "balance",
            }}
          >
            Two designs from the same AI model. The difference?{" "}
            {/* The script accent, not italics — one hand-written phrase per
                screen, closing the sentence as a margin note rather than
                interrupting it. letterSpacing has to be reset: the heading
                runs -0.06em, which on a hand face collides the letters. */}
            <span
              style={{
                fontFamily: "var(--font-hand, 'Crayonize', cursive)",
                fontWeight: 700,
                fontSize: "1.15em",
                lineHeight: 1,
                letterSpacing: 0,
                color: ACCENT,
                display: "inline-block",
                transform: "rotate(-2deg)",
              }}
            >
              This workshop.
            </span>
          </h2>
        </div>

        {/* Slider — full container width */}
        <div
          ref={sliderRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="cdw-ba-slider"
          style={sliderInline}
          role="group"
          aria-label="Before and after slider"
        >
          {/* BEFORE — bottom layer, full bleed */}
          <picture className="cdw-ba-pic-before">
            <source type="image/avif" srcSet={BA_BEFORE_AVIF} />
            <source
              type="image/webp"
              srcSet={`${BA_BEFORE_WEBP_800} 800w, ${BA_BEFORE_WEBP} 1600w`}
              sizes="(max-width: 767px) 100vw, 1340px"
            />
            <img
              className="cdw-ba-img cdw-ba-img-before"
              src={BA_BEFORE_WEBP}
              alt="Before — generic AI-generated design"
              loading="lazy"
              decoding="async"
              width={1600}
              height={900}
              draggable={false}
            />
          </picture>

          {/* AFTER — top layer, clipped by --pos */}
          <picture className="cdw-ba-pic-after">
            <source type="image/avif" srcSet={BA_AFTER_AVIF} />
            <source
              type="image/webp"
              srcSet={`${BA_AFTER_WEBP_800} 800w, ${BA_AFTER_WEBP} 1600w`}
              sizes="(max-width: 767px) 100vw, 1340px"
            />
            <img
              className="cdw-ba-img cdw-ba-img-after"
              src={BA_AFTER_WEBP}
              alt="After — workshop-quality design"
              loading="lazy"
              decoding="async"
              width={1600}
              height={900}
              draggable={false}
            />
          </picture>

          {/* Handle (hairline + circular knob) */}
          <div className="cdw-ba-handle">
            <div className="cdw-ba-knob" aria-hidden="true">
              <svg width="10" height="14" viewBox="0 0 10 14" fill="none">
                <path d="M3 1L1 7l2 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <svg width="10" height="14" viewBox="0 0 10 14" fill="none">
                <path d="M7 1l2 6-2 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="cdw-ba-drag-hint" aria-hidden="true">Drag me</div>
          </div>

          {/* Corner labels */}
          <div className="cdw-ba-labels">
            <span className="cdw-ba-lbl">Before</span>
            <span className="cdw-ba-lbl">After</span>
          </div>
        </div>
      </div>

      <style>{`
        /* BEFORE picture — bottom layer, fills slider */
        .cdw-ba-slider .cdw-ba-pic-before {
          position: absolute;
          inset: 0;
          z-index: 1;
          display: block;
          pointer-events: none;
        }
        .cdw-ba-slider .cdw-ba-img-before {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
          user-select: none;
        }

        /* AFTER picture — top layer, clipped by --pos */
        .cdw-ba-slider .cdw-ba-pic-after {
          position: absolute;
          inset: 0;
          z-index: 2;
          clip-path: polygon(var(--pos, 0%) 0, 100% 0, 100% 100%, var(--pos, 0%) 100%);
          pointer-events: none;
          display: block;
        }
        .cdw-ba-slider .cdw-ba-img-after {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
          user-select: none;
        }

        /* Handle (hairline + circular knob) — both in house terra */
        .cdw-ba-handle {
          position: absolute;
          top: 0;
          bottom: 0;
          left: var(--pos, 0%);
          transform: translateX(-50%);
          width: 2px;
          background: ${ACCENT};
          pointer-events: none;
          z-index: 3;
        }
        .cdw-ba-knob {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: ${ACCENT};
          color: ${ON_ACCENT};
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 3px;
          pointer-events: none;
          transition: transform .18s ease;
          animation: cdw-ba-knob-pulse 3s ease-in-out 2.4s infinite;
        }
        @keyframes cdw-ba-knob-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50%      { transform: translate(-50%, -50%) scale(1.06); }
        }
        @media (hover: hover) {
          .cdw-ba-slider:hover .cdw-ba-knob {
            animation: none;
            transform: translate(-50%, -50%) scale(1.04);
          }
        }
        .cdw-ba-slider.is-dragging {
          cursor: grabbing;
        }
        .cdw-ba-slider.is-dragging .cdw-ba-knob {
          animation: none;
          transform: translate(-50%, -50%) scale(1.08);
        }
        @media (prefers-reduced-motion: reduce) {
          .cdw-ba-knob {
            animation: none;
            transition: none;
          }
        }

        /* "Drag me" hint — sits below the knob, fades out after first interaction */
        .cdw-ba-drag-hint {
          position: absolute;
          top: calc(50% + 42px);
          left: 50%;
          transform: translateX(-50%);
          font-family: var(--font-mono);
          font-size: 10px;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: #fff;
          background: rgba(0, 0, 0, 0.72);
          padding: 5px 11px;
          border-radius: 9999px;
          pointer-events: none;
          white-space: nowrap;
          font-weight: 500;
          opacity: 0.95;
          transition: opacity .25s ease;
        }
        .cdw-ba-slider.has-interacted .cdw-ba-drag-hint {
          opacity: 0;
        }

        /* Corner labels */
        .cdw-ba-labels {
          position: absolute;
          bottom: 14px;
          left: 0;
          right: 0;
          display: flex;
          justify-content: space-between;
          padding: 0 12px;
          pointer-events: none;
          z-index: 4;
        }
        .cdw-ba-lbl {
          background: rgba(0, 0, 0, 0.72);
          color: #fff;
          font-family: var(--font-mono);
          font-size: 9px;
          letter-spacing: 0.26em;
          text-transform: uppercase;
          font-weight: 500;
          padding: 5px 11px;
          border-radius: 9999px;
        }

        /* Responsive */
        @media (max-width: 1100px) {
          [data-section="before-after"] > div {
            padding: 0 24px !important;
          }
        }
        @media (max-width: 640px) {
          [data-section="before-after"] {
            padding: 80px 0 !important;
          }
          [data-section="before-after"] > div {
            padding: 0 20px !important;
          }
          .cdw-ba-knob {
            width: 50px !important;
            height: 50px !important;
          }
          .cdw-ba-drag-hint {
            top: calc(50% + 39px);
          }
          /* On mobile we don't show all three labels at once — pre-interaction
             only "Drag me" shows; once the user drags, swap to Before/After. */
          .cdw-ba-labels {
            opacity: 0;
            transition: opacity .25s ease;
          }
          .cdw-ba-slider.has-interacted .cdw-ba-labels {
            opacity: 1;
          }
        }
      `}</style>
    </section>
  );
}
